import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Termin } from 'api/termin';
import classnames from 'classnames';
import { CheckboxCompositeIcon, CheckboxIcon } from '@fluentui/react-icons';
import { PrimaryButton } from '@fluentui/react';
import nl2br from 'react-nl2br';
import { authSelector } from 'store/auth/selector';
import { useBetreuerByKz } from 'api/betreuer';
import { TerminArtLabel } from 'utils/labels';
import { formatSmDateTime } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import { Timestamps } from 'formComponents/Timestamps';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { einheitenByCodeSelector } from 'store/lookups/selector';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
    },
    shortLineWithKunde: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 110px 100px 150px 1fr max-content max-content',
        gridColumnGap: 10,
    },
    shortLineWithoutKunde: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 120px 100px 150px 3fr max-content max-content',
        gridColumnGap: 10,
    },
    index: {
        position: 'absolute',
        bottom: 5,
        right: 5,
    },
});

interface TerminItemProps {
    termin: Termin;
    index?: number;
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    editTermin: () => void;
}

export const TerminItem: React.FC<TerminItemProps> = ({ termin, index, withKunde = true, viewMode, editTermin }) => {
    const { kunde } = termin;
    const auth = useSelector(authSelector);
    const betreuerByKz = useBetreuerByKz();
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={termin.anlageDat}
            createdDecimalTime={termin.anlageUhr}
            createdUser={termin.anlageUser}
            modifiedDate={termin.updDat}
            modifiedDecimalTime={termin.updUhr}
            modifiedUser={termin.updUser}
            index={index}
        />
    );

    const renderStatus = () => (
        <div>
            {termin.terminStatus ? (
                <CheckboxCompositeIcon className={formStyles.checkbox} />
            ) : (
                <CheckboxIcon className={formStyles.checkbox} />
            )}
        </div>
    );

    const renderEditButton = () => (
        <div>
            {(!termin.terminStatus || auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editTermin();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const renderEinhMenge = () => {
        if (termin.posMenge || termin.posEinh) {
            return (
                '(' +
                termin.posMenge +
                ' x ' +
                (termin.posEinh ? einheitenByCode[termin.posEinh].text || termin.posEinh : '') +
                ')'
            );
        }
        return '';
    };

    const containerClassName = classnames(styles.container, formStyles.listItemShort, {
        [formStyles.archived]: termin.terminStatus,
        [formStyles.important]: termin.prodInfo,
        [formStyles.bearbeiten]: termin.terminArt === 'B',
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                {index !== undefined && <div className={styles.index}>{index}</div>}
                <div className={withKunde ? styles.shortLineWithKunde : styles.shortLineWithoutKunde}>
                    <div>
                        {withKunde && (
                            <div
                                className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                                onClick={() => {
                                    history.push('/kunden/' + kunde.nr);
                                }}
                            >
                                {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                            </div>
                        )}
                        <div>{termin.kaName}</div>
                    </div>
                    <div className={formStyles.shortValue}>
                        {formatSmDateTime(termin.datumVon, termin.zeitVon, true)}
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{termin?.terminArt && (TerminArtLabel[termin.terminArt] || termin.terminArt)}</strong>
                    </div>

                    <div className={formStyles.shortValue}>
                        {termin.userKz &&
                            (betreuerByKz[termin.userKz]
                                ? betreuerByKz[termin.userKz].name || termin.userKz
                                : termin.userKz)}
                    </div>
                    <div className={formStyles.shortValue}>
                        {termin.prodInfo && (
                            <strong>
                                Strategische Info!
                                <br />
                            </strong>
                        )}
                        <strong>
                            {termin.terminBetreff} {renderEinhMenge()}
                        </strong>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
            </div>
        );
    }

    return (
        <div className={containerClassName}>
            <div className={formStyles.topBlock}>
                <div>
                    <div
                        className={classnames(formStyles.listTitle, formStyles.clickable)}
                        onClick={() => {
                            history.push('/kunden/' + kunde.nr);
                            // openDetails();
                        }}
                    >
                        {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                    </div>
                </div>
                {renderStatus()}
                {renderEditButton()}
            </div>
            <div className={formStyles.twoColumnsBlock}>
                <div className={formStyles.firstColumn}>
                    <div className={formStyles.line}>
                        {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                    </div>
                    <div className={formStyles.line}>Ansprechpartner: {termin.kaName}</div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey}>MA:</div>
                        <div className={formStyles.dataValue}>
                            <strong>
                                {termin.userKz &&
                                    (betreuerByKz[termin.userKz]
                                        ? betreuerByKz[termin.userKz].name || termin.userKz
                                        : termin.userKz)}
                            </strong>
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey}>Art:</div>
                        <div className={formStyles.dataValue}>
                            <strong>
                                {termin?.terminArt && (TerminArtLabel[termin.terminArt] || termin.terminArt)}
                            </strong>
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey}>Von: </div>
                        <div className={formStyles.dataValue}>
                            {formatSmDateTime(termin.datumVon, termin.zeitVon, true)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey}>Bis: </div>
                        <div className={formStyles.dataValue}>
                            {formatSmDateTime(termin.datumBis, termin.zeitBis, true)}
                        </div>
                    </div>
                    <div className={formStyles.keyValueLine}>
                        <div className={formStyles.dataKey}>Ort: </div>
                        <div className={formStyles.dataValue}>{termin.terminOrt}</div>
                    </div>
                    {termin.prodInfo && (
                        <div className={formStyles.keyValueLine}>
                            <div>Strategische Info!</div>
                        </div>
                    )}
                    {(termin.mailKd || termin.mailKdBem || termin.mailPdf) && (
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey}>Mail:</div>
                            <div className={formStyles.dataValue}>
                                {termin.mailKd && 'Mail an Kunden '}
                                {termin.mailKdBem && 'mit Beschreibung '}
                                {termin.mailPdf && 'mit Kundendetails als PDF '}
                            </div>
                        </div>
                    )}
                </div>
                <div className={formStyles.thirdColumn}>
                    <div className={formStyles.line}>
                        <strong>
                            {termin.terminBetreff} {renderEinhMenge()}
                        </strong>
                    </div>
                    <div className={formStyles.line}>{nl2br(termin.terminText || '')}</div>
                </div>
            </div>
            {renderTimestamps()}
        </div>
    );
};
